import { BaseDataForm } from './BaseDataForm';
import { IngredientGroupsForm } from './IngredientGroupsForm';
import { InstructionsForm } from './InstructionsForm';
import { TagsForm } from './TagsForm';
import { useEffect, useRef, useState } from 'react';
import { UI } from '@/Components/UI';
import { useForm } from '@/Contexts';
import { useTranslation } from 'react-i18next';

enum RecipeSteps {
    BASE,
    INGREDIENTS,
    INSTRUCTIONS,
    TAGS,
}

export const RecipeForm = () => {
    const { t } = useTranslation(['recipes']);
    const [activeStep, setActiveStep] = useState<RecipeSteps>(RecipeSteps.BASE);

    const form = useForm();

    if (!form) {
        throw new Error('RecipeForm must be used within a FormProvider');
    }

    const { errors, data } = form;

    const formData = useRef(data);

    useEffect(() => {
        // Check if form data has not changed (->form was submitted with new errors)
        if (formData.current !== data) {
            formData.current = data;
            return;
        }

        // Check if there are any errors
        if (Object.keys(errors).length === 0) {
            return;
        }

        // Map the errors to the corresponding step
        const errorPages = Object
            .keys(errors)
            .map(key => {
                if (key.startsWith('ingredientGroups')) {
                    return RecipeSteps.INGREDIENTS;
                } else if (key.startsWith('instructions')) {
                    return RecipeSteps.INSTRUCTIONS;
                } else if (key.startsWith('tags')) {
                    return RecipeSteps.TAGS;
                } else {
                    return RecipeSteps.BASE;
                }
            });

        // Get the first error
        const firstError: RecipeSteps = Math.min(...errorPages);

        // Set the active step to the first error
        if (firstError !== activeStep) {
            setActiveStep(firstError);
        }
    }, [errors, data]);

    return (
        <>
            <UI.Stepper>
                <UI.Stepper.Item
                    title={t('form.baseData.heading')}
                    isActive={activeStep === RecipeSteps.BASE}
                    onClick={() => setActiveStep(RecipeSteps.BASE)}>
                    <UI.Icon name={'description'} />
                </UI.Stepper.Item>

                <UI.Stepper.Item
                    title={t('form.ingredients.heading')}
                    isActive={activeStep === RecipeSteps.INGREDIENTS}
                    onClick={() => setActiveStep(RecipeSteps.INGREDIENTS)}>
                    <UI.Icon name={'egg_alt'} />
                </UI.Stepper.Item>

                <UI.Stepper.Item
                    title={t('form.instructions.heading')}
                    isActive={activeStep === RecipeSteps.INSTRUCTIONS}
                    onClick={() => setActiveStep(RecipeSteps.INSTRUCTIONS)}>
                    <UI.Icon name={'list'} />
                </UI.Stepper.Item>

                <UI.Stepper.Item
                    title={t('form.tags.heading')}
                    isActive={activeStep === RecipeSteps.TAGS}
                    onClick={() => setActiveStep(RecipeSteps.TAGS)}>
                    <UI.Icon name={'tag'} />
                </UI.Stepper.Item>
            </UI.Stepper>

            {activeStep === RecipeSteps.BASE && (
                <>
                    <BaseDataForm />

                    <div className={'grid grid-cols-2 gap-2 mt-auto'}>
                        <div></div>

                        <UI.Button
                            type={'button'}
                            onClick={() => setActiveStep(RecipeSteps.INGREDIENTS)}>
                            {t('misc:next')}
                        </UI.Button>
                    </div>
                </>
            )}

            {activeStep === RecipeSteps.INGREDIENTS && (
                <>
                    <IngredientGroupsForm />

                    <div className={'grid grid-cols-2 gap-2 mt-auto'}>
                        <UI.Button
                            buttonStyle={'outline'}
                            type={'button'}
                            onClick={() => setActiveStep(RecipeSteps.BASE)}>
                            {t('misc:previous')}
                        </UI.Button>

                        <UI.Button
                            type={'button'}
                            onClick={() => setActiveStep(RecipeSteps.INSTRUCTIONS)}>
                            {t('misc:next')}
                        </UI.Button>
                    </div>
                </>
            )}

            {activeStep === RecipeSteps.INSTRUCTIONS && (
                <>
                    <InstructionsForm />

                    <div className={'grid grid-cols-2 gap-2 mt-auto'}>
                        <UI.Button
                            buttonStyle={'outline'}
                            type={'button'}
                            onClick={() => setActiveStep(RecipeSteps.INGREDIENTS)}>
                            {t('misc:previous')}
                        </UI.Button>

                        <UI.Button
                            type={'button'}
                            onClick={() => setActiveStep(RecipeSteps.TAGS)}>
                            {t('misc:next')}
                        </UI.Button>
                    </div>
                </>
            )}

            {activeStep === RecipeSteps.TAGS && (
                <>
                    <TagsForm />

                    <div className={'grid grid-cols-2 gap-2 mt-auto'}>
                        <UI.Button
                            buttonStyle={'outline'}
                            type={'button'}
                            onClick={() => setActiveStep(RecipeSteps.INSTRUCTIONS)}>
                            {t('misc:previous')}
                        </UI.Button>

                        <UI.Button
                            type={'submit'}
                            disabled={form.processing}>
                            {t('misc:save')}
                        </UI.Button>
                    </div>
                </>
            )}
        </>
    );
};
